import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import {
  ConfirmResult,
  useConfirmModal,
} from '@/components/lemmala-design-system/modal/use-confirm-modal'

interface UseUnsavedChangesGuardProps {
  hasUnsavedChanges: boolean
  /** Returning false means the save was aborted; the guard then stays put. */
  onSave?: () => void | false | Promise<void | false>
  pageName: string
}

export const useUnsavedChangesGuard = ({
  hasUnsavedChanges,
  onSave,
  pageName,
}: UseUnsavedChangesGuardProps) => {
  const { confirm } = useConfirmModal()
  const navigate = useNavigate()
  const { updateParam, removeParam } = useUrlParams()
  const title = 'Unsaved changes'
  const content = `You've made changes to your ${pageName}. If you leave now, your changes will be lost.`
  const isLeavingRef = useRef(false)

  useEffect(() => {
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.href)
    }
  }, [hasUnsavedChanges])

  const showUnsavedChangesModal = useCallback(
    async (onLeave: () => void) => {
      const result = await confirm({
        title,
        content,
        primaryButtonText: onSave ? 'Save changes and leave' : 'Stay and continue editing',
        cancelButtonText: 'Leave without saving',
      })
      if (result === ConfirmResult.DISMISSED || (result === ConfirmResult.CONFIRMED && !onSave)) {
        return
      }
      if (result === ConfirmResult.CONFIRMED && onSave) {
        // The save can still abort (a nested confirm was cancelled, validation failed);
        // navigating away then would drop the edits the user just chose to keep.
        if ((await onSave()) === false) return
      }
      onLeave()
    },
    [confirm, content, onSave],
  )

  const handleNavigationAttempt = useCallback(
    (event: CustomEvent<{ newSection?: string; paramName?: string; handled?: boolean }>) => {
      const detail = event.detail ?? {}
      if (!hasUnsavedChanges || detail.handled) return
      detail.handled = true
      event.preventDefault()
      showUnsavedChangesModal(() => {
        const param = (detail.paramName as UrlParam) ?? UrlParam.SETTING_SECTION
        if (detail.newSection) {
          updateParam(param, detail.newSection)
        } else {
          removeParam(param)
        }
      })
    },
    [hasUnsavedChanges, showUnsavedChangesModal, updateParam, removeParam],
  )

  useEffect(() => {
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = ''
        return ''
      }
      return undefined
    }

    const handlePopState = (event: PopStateEvent) => {
      if (!hasUnsavedChanges || isLeavingRef.current) return
      event.preventDefault()
      // Back popped the guard entry; restore it so staying keeps the user on this page.
      window.history.pushState(null, '', window.location.href)

      showUnsavedChangesModal(() => {
        // Step over the guard entry and this page to wherever Back was heading.
        // document.referrer can't be used: in an SPA it is the referrer of the first page load.
        isLeavingRef.current = true
        if (window.history.length > 2) {
          window.history.go(-2)
        } else {
          navigate('/', { replace: true })
        }
      })
    }

    const handleLinkClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest('a')
      if (!link || !hasUnsavedChanges) return

      const href = link.getAttribute('href')
      if (!href || href.startsWith('#')) return

      const target = link.getAttribute('target')?.toLowerCase()
      if (target && target !== '_self') return

      if (link.hasAttribute('download')) return

      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      showUnsavedChangesModal(() => {
        navigate(href)
      })
    }

    window.addEventListener('popstate', handlePopState)
    document.addEventListener('click', handleLinkClick, true)
    window.addEventListener('beforeunload', beforeUnloadHandler)
    document.addEventListener('checkUnsavedChanges', handleNavigationAttempt as EventListener)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      document.removeEventListener('click', handleLinkClick, true)
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      document.removeEventListener('checkUnsavedChanges', handleNavigationAttempt as EventListener)
    }
  }, [hasUnsavedChanges, showUnsavedChangesModal, navigate, handleNavigationAttempt])
}
